import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Button, Typography, Row, Col, Table, Divider, notification, Input, Select, Tag, Tooltip } from "antd";
import EventForm from "./EventForm";
import axios from "axios";
import "./Event.css";
import { useTranslation } from 'react-i18next';

const { Title } = Typography;
const { Search } = Input;

const Events = props => {
  const [currentPageEvents, setCurrentPageEvents] = useState([]);
  const [listState, setListState] = useState({
    pagination: { pageSize: 10, current: 1 },
    filters: {},
    sorter: {}
  });
  const [selectOptions, setSelectOptions] = useState({ "severity": [], "types": [] });
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [editState, setEditState] = useState({ event: {}, valid: false });
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const startLoading = () => dispatch({ type: "START_LOADING" });
  const stopLoading = (val) => dispatch({ type: "STOP_LOADING", status: val });

  const handleTableChange = (pagination, filters, sorter) => {
    // Convert antd's sorter object to a format we can use
    const sortConfig = sorter.field ? {
      field: sorter.field,
      order: sorter.order // 'ascend' or 'descend'
    } : {};

    setListState({
      pagination: pagination,
      filters: filters,
      sorter: sortConfig
    });
  };

  useEffect(() => {
    // Create an object to store our enum values
    const options = {
      types: [],
      severity: []
    };

    // Fetch event types
    const fetchTypes = axios.get(process.env.REACT_APP_API_ENDPOINT + "/events/types");

    // Fetch event severities
    const fetchSeverities = axios.get(process.env.REACT_APP_API_ENDPOINT + "/events/severities");

    // Execute both requests in parallel
    Promise.all([fetchTypes, fetchSeverities])
      .then(([typesResponse, severitiesResponse]) => {
        options.types = typesResponse.data;   // .data is the body of the response
        options.severity = severitiesResponse.data;  // .data is the body of the response
        setSelectOptions(options);
      })
      .catch(function (error) {
        console.log(error);
      });
  }, []);

  const saveEvent = () => {
    // Only save if in edit mode and data is valid
    if (!isEditMode || !editState.valid) {
      return;
    }

    console.log("[EVENT] = " + JSON.stringify(editState));
    startLoading();

    // Determine whether to create a new event or update an existing one
    const isNewEvent = editState.event.id === -1;
    const method = isNewEvent ? 'post' : 'put';
    const endpoint = isNewEvent
      ? process.env.REACT_APP_API_ENDPOINT + "/events/"
      : process.env.REACT_APP_API_ENDPOINT + "/events/" + editState.event.id;

    // If creating a new event, ensure id is null as backend expects
    const eventData = { ...editState.event };
    if (isNewEvent) {
      eventData.id = null;
    }

    axios[method](endpoint, eventData)
      .then((response) => {
        const savedEvent = response.data;
        // Update the selected row key to match the saved event
        setSelectedRowKey(savedEvent.id);
        setIsEditMode(false);

        // Reload events from server, which will also reselect this event with fresh data
        reloadEvents();

        setTimeout(() => {
          stopLoading("success");
        }, 1000);
      })
      .catch(function (error) {
        console.log(error);
        setTimeout(() => {
          stopLoading("failure");
        }, 1000);
      });
  };

  const newEvent = () => {
    // Only change the page if needed
    if (listState.pagination.current !== 1) {
      // First update pagination to page 1 and let the data reload
      setListState({
        ...listState,
        pagination: {
          ...listState.pagination,
          current: 1
        }
      });

      // After the reload is complete, then create the new event
      // We use setTimeout to ensure this runs after the page change and data reload
      setTimeout(() => {
        const newEvent = { id: -1 };
        setCurrentPageEvents([newEvent, ...currentPageEvents]);
        setEditState({ event: newEvent, valid: false });
        setSelectedRowKey(-1);
        setIsEditMode(true);
      }, 500); // Short delay to let the reload complete
    } else {
      // If already on page 1, just create the new event without changing pages
      const newEvent = { id: -1 };
      setCurrentPageEvents([newEvent, ...currentPageEvents]);
      setEditState({ event: newEvent, valid: false });
      setSelectedRowKey(-1);
      setIsEditMode(true);
    }
  };

  const cancelEdit = () => {
    // If we're canceling a new event, remove it from the temporary display
    if (editState.event.id === -1) {
      // First remove the temporary new event from the current page
      setCurrentPageEvents(currentPageEvents.filter(event => event.id !== -1));
      setSelectedRowKey(null);
      setEditState({ event: {}, valid: false });
    } else {
      // If we're canceling an edit to an existing event, remember its ID
      const eventId = editState.event.id;
      // We'll reselect this event after reloading
      setSelectedRowKey(eventId);
    }

    setIsEditMode(false);

    // Reload the data from the server in all cases
    // This ensures we have the latest data and proper selection state
    reloadEvents();
  };

  const editEvent = id => {
    const toEdit = currentPageEvents.find(i => i.id === id);
    // Create a deep copy to avoid reference issues
    const toEditCopy = JSON.parse(JSON.stringify(toEdit));
    setEditState({ event: toEditCopy, valid: true });
    setIsEditMode(true);
  };

  const deleteEvent = id => {
    const toDelete = currentPageEvents.find(i => i.id === id);
    startLoading();

    // Use the new RESTful endpoint for delete
    axios
      .delete(`${process.env.REACT_APP_API_ENDPOINT}/events/${toDelete.id}`)
      .then((response) => { // ignored response
        reloadEvents();
        setEditState({ event: {}, valid: false });
        setTimeout(() => {
          stopLoading("success");
        }, 1000);
      })
      .catch(function (error) {
        console.log(error);
        setTimeout(() => {
          stopLoading("failure");
        }, 1000);
      })
      .finally(function () { });
  };

  const handleSearch = (value) => {
    // Reset to first page when searching
    setListState({
      ...listState,
      pagination: {
        ...listState.pagination,
        current: 1
      },
      filters: {
        ...listState.filters,
        name: value ? [value] : undefined
      }
    });
  };

  const handleTypeFilterChange = (value) => {
    setListState({
      ...listState,
      pagination: {
        ...listState.pagination,
        current: 1
      },
      filters: {
        ...listState.filters,
        type: value ? [value] : undefined
      }
    });
  };

  const eventChange = (newRecord, valid) => {
    var newEvent = { ...editState };
    // Update the event object with values from the form
    newEvent.event.name = newRecord.name.value;
    newEvent.event.type = newRecord.type.value;
    // Severity field hidden from UI but preserved for backward compatibility with existing data
    if (newRecord.severity) {
      newEvent.event.severity = newRecord.severity.value;
    }
    const dateValue = newRecord.date.value;
    const dateEndValue = newRecord.dateEnd.value;
    newEvent.event.date = dateValue ? dateValue.format() : null;
    newEvent.event.dateEnd = dateEndValue ? dateEndValue.format() : null;
    newEvent.event.geometry = newRecord.geometry.value;
    newEvent.event.country = newRecord.country?.value;
    newEvent.event.state = newRecord.state?.value;
    newEvent.event.county = newRecord.county?.value;
    newEvent.event.town = newRecord.town?.value;
    newEvent.event.hamlet = newRecord.hamlet?.value;
    newEvent.event.postcode = newRecord.postcode?.value;
    newEvent.event.road = newRecord.road?.value;
    newEvent.event.houseNumber = newRecord.houseNumber?.value;
    newEvent.event.coordinatesInferred = newRecord.coordinatesInferred?.value;
    newEvent.event.parentEventId = newRecord.parentEventId?.value ?? null;

    newEvent.valid = valid;
    setEditState(newEvent);
  };

  const reloadEvents = useCallback(() => {
    // Memorize the currently selected event ID before reloading
    const currentSelectedId = selectedRowKey;

    setIsLoading(true);
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/events/", {
        params: {
          page: listState.pagination.current - 1,  // Spring uses 0-based page indexing
          size: listState.pagination.pageSize,
          // Convert the sort parameters to Spring format
          sort: listState.sorter.field ?
            `${listState.sorter.field},${listState.sorter.order === 'descend' ? 'desc' : 'asc'}` :
            undefined,
          // Pass individual filter parameters
          name: listState.filters.name?.[0],
          severity: listState.filters.severity?.[0],
          type: listState.filters.type?.[0],
          // add fromDate and toDate here if using them
        }
      })
      // In reloadEvents function, change the response handling
      .then((response) => {
        // Change from response.data.data to response.data.content
        const newEvents = response.data.content;
        console.log("Events loaded from backend:", newEvents);
        setCurrentPageEvents(newEvents);
        // Change from response.data.totalCount to response.data.totalElements
        setTotalCount(response.data.totalElements);

        // If there was a selected event, find it in the new data and update editState
        if (currentSelectedId !== null && currentSelectedId !== -1) {
          const selectedEvent = newEvents.find(event => event.id === currentSelectedId);
          if (selectedEvent) {
            setEditState({ event: selectedEvent, valid: true });
            setSelectedRowKey(currentSelectedId);
          }
        }
      })
      .catch(function (error) {
        console.log(error);
      })
      .finally(function () {
        setIsLoading(false);
      });
  }, [listState, selectedRowKey]);

  const importEvents = () => {
    notification.open({
      placement: "bottomRight",
      message: 'RETRIEVE DATA FROM EXTERNAL DATABASES ',
      description:
        'Function currently not available.',
    });
  };

  useEffect(() => {
    reloadEvents();
  }, [listState, reloadEvents]);

  const showEvent = id => {
    const toShow = currentPageEvents.find(i => i.id === id);
    console.log("Event selected for display:", toShow);
    setEditState({ event: toShow, valid: true });
    setIsEditMode(false);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      width: "37%"
    },
    {
      title: "Type",
      dataIndex: "type",
      width: "18%",
      sorter: true,
      render: (text) => text ? t(`events.type.${text}`) : ""  // null check
    },
    {
      title: "Multi-Hazard",
      key: "multiHazard",
      width: "15%",
      align: "center",
      render: (text, record) => {
        if (record.type === 'MULTI_HAZARD_EVENT') {
          return <Tooltip title="Multi-hazard event"><Tag color="blue">Multi-hazard</Tag></Tooltip>;
        }
        if (record.parentEventId) {
          return (
            <Tooltip title={record.parentEventName ? `Part of: ${record.parentEventName}` : 'Part of a multi-hazard event'}>
              <Tag color="orange">Part of</Tag>
            </Tooltip>
          );
        }
        return null;
      }
    },
    //date
    {
      title: "Date",
      dataIndex: "date",
      width: "15%",
      sorter: true,
      render: (text) => text ? new Date(text).toLocaleDateString() : ""  // null check
    },
    {
      title: "Action",
      key: "action",
      width: "15%",
      render: (text, record) => (
        <span>
          <Button
            type="default"
            size="small"
            disabled={isEditMode}
            onClick={(e) => {
              e.stopPropagation(); // Stop the click event from propagating to parent elements
              editEvent(record.id);
            }}

          >
            Edit
          </Button>
          <Divider type="vertical" />
          <Button
            type="default"
            size="small"
            disabled={isEditMode}
            onClick={() => deleteEvent(record.id)}
          >
            Delete
          </Button>
        </span>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3}>Events</Title>
        </Col>
        <Col>
          <Button key="1"
            disabled={isEditMode}
            onClick={() => reloadEvents()}>
            Refresh
          </Button>
          <Button key="2"
            style={{ marginLeft: 8 }}
            disabled={isEditMode}
            onClick={() => newEvent()}>
            New
          </Button>
          <Button
            key="3"
            style={{ marginLeft: 8 }}
            type="primary"
            disabled={!isEditMode || !editState.valid}
            onClick={() => saveEvent()}>
            Save
          </Button>
          <Button
            key="5"
            style={{ marginLeft: 8 }}
            onClick={() => cancelEdit()}
            disabled={!isEditMode}>
            Cancel
          </Button>
          <Button
            key="4"
            style={{ marginLeft: 8 }}
            disabled={isEditMode}
            onClick={() => importEvents()}>
            Sync RDH
          </Button>
        </Col>
      </Row>
      <Row style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Search
            placeholder="Search events by name"
            allowClear
            onSearch={handleSearch}
            style={{ width: '100%' }}
            disabled={isEditMode}
          />
        </Col>
        <Col span={8} offset={1}>
          <Select
            placeholder="Filter by event type"
            style={{ width: '100%' }}
            onChange={handleTypeFilterChange}
            allowClear
            disabled={isEditMode}
          >
            {selectOptions.types?.map(eventType => (
              <Select.Option key={eventType} value={eventType}>
                {t(`events.type.${eventType}`)}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={24} style={{ height: 250 }}>
          <Table
            size="small"
            scroll={{ y: 150 }}
            columns={columns}
            rowKey={event => event.id}
            dataSource={currentPageEvents}
            // In the Table component, update the pagination prop
            pagination={{
              pageSize: listState.pagination.pageSize,
              current: listState.pagination.current,
              // Change from totalCount to response.data.totalElements
              total: totalCount,
              // Add these properties to make it more compatible with Spring's Page
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} events`,
              // Use 0-based pagination for backend but 1-based for UI
              onChange: (page, pageSize) => {
                setListState({
                  ...listState,
                  pagination: {
                    ...listState.pagination,
                    current: page,
                    pageSize: pageSize
                  }
                });
              }
            }}
            loading={isLoading}
            onChange={(pagination, filters, sorter) => handleTableChange(pagination, filters, sorter)}
            onRow={(record) => ({
              onClick: () => {
                if (!isEditMode) {
                  showEvent(record.id);
                  setSelectedRowKey(record.id);
                }
              },
              className: record.id === selectedRowKey ? 'selected-row' : '',
              style: isEditMode ? { cursor: 'not-allowed' } : {}
            })}
            rowClassName={(event) => event.id === selectedRowKey ? 'selected-row' : ''}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <EventForm record={editState} selectOption={selectOptions}
            parentCallback={eventChange} isEditMode={isEditMode} />
        </Col>
      </Row>
    </div>
  );
};
export default Events;